import React, { memo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Loader, Text } from '@Sage/hoi-poi-ui';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import EmailTemplatesEmpty from 'components/SvgIcons/emptyScreen/EmailTemplatesEmpty';
import { CAMPAIGNS_MARKETING, CAMPAIGNS_TEMPLATES } from 'constants/Entities';
import { EntityCrudService } from 'services';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import { mergeTopolJsonIntoCampaignMetadata } from '../../utils/marketingCampaignMetadata';
import { MARKETING_CAMPAIGN_STATUS } from '../../utils/marketingCampaignStatus';
import TemplatesEmptyView from '../components/TemplatesEmptyView';
import PredefinedTemplateCard from '../create/components/PredefinedTemplateCard';
import { useMarketingTemplates } from '../hooks/useMarketingTemplates';
import { MARKETING_TEMPLATES_PATHS } from '../marketingTemplatesPaths';
import { useMarketingTemplatesRefresh } from '../MarketingTemplatesRefreshContext';

import '../create/styles.scss';
import './MarketingTemplatesList.scss';

function getCreatedCampaignId(result) {
    const id = result?.id;
    if (id === null || id === undefined || id === '') {
        return null;
    }
    const numeric = Number(id);
    return Number.isNaN(numeric) ? id : numeric;
}

function buildCampaignCreatePayloadFromTemplate(template) {
    const name = (template?.name || '').trim();
    const body = template?.strBody || '';
    const rawJson = template?.strJson;
    const jsonStr = typeof rawJson === 'string' ? rawJson.trim() : '';
    const looksLikeTopolJson = jsonStr.startsWith('{') || jsonStr.startsWith('[');

    const metadata = looksLikeTopolJson ? mergeTopolJsonIntoCampaignMetadata(null, rawJson) : null;

    return {
        name,
        senderId: null,
        subject: '',
        body,
        status: MARKETING_CAMPAIGN_STATUS.DRAFT,
        previewText: '',
        ...(metadata && typeof metadata === 'object' && !Array.isArray(metadata)
            ? { metadata }
            : {}),
    };
}

const MarketingTemplatesList = memo(({ onOpenHtmlEditor, onOpenVisualEditor }) => {
    const navigate = useNavigate();
    const searchQuery = useSelector(
        (state) =>
            state.entityFilters?.[CAMPAIGNS_TEMPLATES.entity]?.filters?.matchingName?.value ?? '',
    );

    const { templates, isLoading, error, hasMore, loadMore, refetch } = useMarketingTemplates({
        searchQuery,
    });

    const { refreshNonce } = useMarketingTemplatesRefresh();

    useEffect(() => {
        if (refreshNonce === 0) {
            return;
        }
        refetch();
    }, [refreshNonce, refetch]);

    const handleTemplateLibraryChanged = useCallback(() => {
        refetch();
    }, [refetch]);

    const createInFlightRef = useRef(false);
    const sentinelRef = useRef(null);

    const emptyViewProps = useCallback(
        () => ({
            icon: <EmailTemplatesEmpty />,
            iconType: 'image',
            title: getLiteral('label_marketing_campaigns_templates_empty'),
            subtitle: getLiteral('label_marketing_campaigns_templates_empty_desc'),
            others: {
                callsToAction: [
                    <Button
                        key="create-first"
                        type="secondary"
                        onClick={() => navigate(MARKETING_TEMPLATES_PATHS.NEW_SELECTOR)}
                    >
                        {getLiteral('label_marketing_campaigns_templates_create_first')}
                    </Button>,
                ],
            },
        }),
        [navigate],
    );

    const handleUseTemplate = useCallback(
        async (template) => {
            if (createInFlightRef.current) {
                return;
            }
            createInFlightRef.current = true;
            try {
                const payload = buildCampaignCreatePayloadFromTemplate(template);
                if (!payload.name) {
                    payload.name = getLiteral('label_email_template');
                }
                const result = await EntityCrudService.create(CAMPAIGNS_MARKETING, payload);
                const savedId = getCreatedCampaignId(result);
                if (savedId != null) {
                    navigate(`${CAMPAIGNS_MARKETING.route}/edit/${savedId}`);
                    return;
                }
                errorToast({ text: getLiteral('error_generalerror') });
            } catch {
                errorToast({ text: getLiteral('error_generalerror') });
            } finally {
                createInFlightRef.current = false;
            }
        },
        [navigate],
    );

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || isLoading) {
            return undefined;
        }

        const scrollRoot = node.closest('.marketing-templates__content');

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting) {
                    loadMore();
                }
            },
            { root: scrollRoot, rootMargin: '120px', threshold: 0 },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, isLoading, loadMore, templates.length]);

    const showInitialError = Boolean(error) && !isLoading && templates.length === 0;
    const showSearchEmpty =
        !isLoading && !error && !templates.length && Boolean((searchQuery || '').trim());
    const showLibraryEmpty =
        !isLoading && !error && !templates.length && !(searchQuery || '').trim();

    return (
        <div className="marketing-templates-list">
            <div className="marketing-templates__content marketing-templates-list__inner">
                {isLoading && (
                    <div className="marketing-templates-list__loader" aria-busy="true">
                        <Loader />
                    </div>
                )}

                {showInitialError && (
                    <div className="marketing-templates-list__error">
                        <Text type="body">{getLiteral('error_generalerror')}</Text>
                        <Button type="secondary" onClick={refetch}>
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                {showSearchEmpty && (
                    <div className="marketing-templates-list__empty-search">
                        <AbsoluteEmpty
                            title={getLiteral('title_templates_not_found')}
                            subtitle={getLiteral('label_search_templates_not_found')}
                            size="small"
                        />
                    </div>
                )}

                {showLibraryEmpty && <TemplatesEmptyView emptyViewProps={emptyViewProps} />}

                {!isLoading && !showInitialError && !showSearchEmpty && !showLibraryEmpty && (
                    <>
                        <div className="marketing-templates-grid">
                            {templates.map((row, index) => (
                                <PredefinedTemplateCard
                                    key={row.id != null ? String(row.id) : `tpl-${index}`}
                                    template={row}
                                    onUseTemplate={handleUseTemplate}
                                    onOpenHtmlEditor={onOpenHtmlEditor}
                                    onOpenVisualEditor={onOpenVisualEditor}
                                    onTemplateLibraryChanged={handleTemplateLibraryChanged}
                                />
                            ))}
                        </div>
                        {hasMore && (
                            <div
                                ref={sentinelRef}
                                className="marketing-templates-list__sentinel"
                                aria-hidden="true"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

MarketingTemplatesList.displayName = 'MarketingTemplatesList';

MarketingTemplatesList.propTypes = {
    onOpenHtmlEditor: PropTypes.func,
    onOpenVisualEditor: PropTypes.func,
};

export default MarketingTemplatesList;
